import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useOndi } from "../../context/OndiContext";
import { OndiWidget } from "./OndiWidget";
import { OndiBubble } from "./OndiBubble";
import { OndiPanel } from "./OndiPanel";
import { OndiTour } from "./OndiTour";
import { OndiVoiceMode } from "../voice/OndiVoiceMode";

const DEFAULT_MARGIN = 20;
const CHARACTER_WIDTH = 76;
const CHARACTER_HEIGHT = 92;
const DRAG_THRESHOLD = 6;

function clampToViewport(x, y) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = Math.min(Math.max(x, DEFAULT_MARGIN), vw - CHARACTER_WIDTH - DEFAULT_MARGIN);
  const cy = Math.min(Math.max(y, DEFAULT_MARGIN + 60), vh - CHARACTER_HEIGHT - DEFAULT_MARGIN - 80);
  return { x: cx, y: cy };
}

function defaultPosition() {
  if (typeof window === "undefined") return { x: 20, y: 20 };
  return clampToViewport(
    window.innerWidth - CHARACTER_WIDTH - 20,
    window.innerHeight - CHARACTER_HEIGHT - 100,
  );
}

export function OndiFloater() {
  const { status } = useAuth();
  const {
    panelOpen,
    togglePanel,
    thinking,
    position,
    savePosition,
    tourActive,
    drivingActive,
    closeDriving,
  } = useOndi();

  const [pos, setPos] = useState(() => position || defaultPosition());
  const [dragging, setDragging] = useState(false);
  const [hovering, setHovering] = useState(false);
  const dragStateRef = useRef(null);

  useEffect(() => {
    if (position) setPos(clampToViewport(position.x, position.y));
  }, [position]);

  useEffect(() => {
    const onResize = () => {
      setPos((p) => clampToViewport(p.x, p.y));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = useCallback((e) => {
    if (e.target.closest("[data-ondi-no-drag]")) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    dragStateRef.current = {
      startX,
      startY,
      origPos: { ...pos },
      moved: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    const st = dragStateRef.current;
    if (!st) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    if (!st.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      st.moved = true;
      setDragging(true);
    }
    if (st.moved) {
      setPos(clampToViewport(st.origPos.x + dx, st.origPos.y + dy));
    }
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      const st = dragStateRef.current;
      dragStateRef.current = null;
      setDragging(false);
      if (!st) return;
      if (!st.moved) {
        // Treat as tap
        togglePanel();
      } else {
        savePosition(pos);
      }
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch (_e) {
        /* ignore */
      }
    },
    [pos, savePosition, togglePanel],
  );

  if (status !== "authed") return null;
  // Hide floater while Modo Conducción overlay owns the screen
  const floaterHidden = drivingActive;
  // Suppress the "¿Qué deseas?" bubble when any ONDI surface takes over
  const bubbleSuppressed = floaterHidden || panelOpen || tourActive || dragging || thinking;

  return (
    <>
      {!floaterHidden && (
      <div
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: CHARACTER_WIDTH,
          height: CHARACTER_HEIGHT,
          zIndex: 55,
          touchAction: "none",
          cursor: dragging ? "grabbing" : "grab",
          transition: dragging ? "none" : "transform 220ms ease",
          transform: hovering && !dragging ? "scale(1.06)" : "scale(1)",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        data-testid="ondi-floater"
        role="button"
        aria-label={panelOpen ? "Cerrar ONDI" : "Abrir ONDI"}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            togglePanel();
          }
        }}
      >
        <div style={{ pointerEvents: "none" }}>
          <OndiWidget
            width={CHARACTER_WIDTH}
            height={CHARACTER_HEIGHT}
            waving={hovering || panelOpen}
            talking={thinking}
          />
        </div>
      </div>
      )}

      {!floaterHidden && (
        <OndiBubble
          anchor={pos}
          characterWidth={CHARACTER_WIDTH}
          characterHeight={CHARACTER_HEIGHT}
          suppressed={bubbleSuppressed}
        />
      )}

      <OndiPanel anchor={pos} />
      {tourActive && <OndiTour />}
      <OndiVoiceMode open={drivingActive} onClose={closeDriving} />
    </>
  );
}
